import { FormKitSchemaFormKit } from "@formkit/core";
import { FormLayoutNode, FormSchema, getCreateForm, getUpdateForm } from "@q-food/q-food";
import { isArray } from "lodash";

/**
 * For a given form name and resourceId, returns the form schema and the original form values within the schema.
 */
function useFormSchema<T>(formName: string, resourceId?: string) {
	const formArrayDefinition = ref<FormKitSchemaFormKit[]>([]);
	const formLayout = ref<FormLayoutNode[] | null>(null);

	/**
	 * The endpoints return `{ fields, layout }`. The envelope is unwrapped here rather than in
	 * `FormPage` so that the print and label modals, which use this composable directly, keep
	 * working untouched.
	 */
	function apply({ fields, layout }: FormSchema) {
		formArrayDefinition.value = fields as FormKitSchemaFormKit[];
		formLayout.value = layout ?? null;
	}

	if (resourceId) {
		getUpdateForm(formName, resourceId).then(apply);
	} else {
		getCreateForm(formName).then(apply);
	}

	const formObjectDefinition = computed(() => {
		const object: { [key in keyof T]: FormKitSchemaFormKit } = {} as any;
		formArrayDefinition.value.forEach((node) => {
			if (node.$formkit && node.name) {
				object[node.name as keyof T] = node;
			}
		});
		return object;
	});

	const originalFormValues = computed(() => {
		const values: Partial<T> = {};
		formArrayDefinition.value.forEach((node) => {
			if (node.$formkit && node.name && node.value != null) {
				if (typeof node.value == "object" && !isArray(node.value)) {
					// @ts-ignore-next-line
					values[node.name] = Object.fromEntries(Object.entries(node.value).filter(([_, v]) => v != null));
				} else {
					// @ts-ignore-next-line
					values[node.name] = node.value;
				}
			}
		});
		return values as T;
	});

	return {
		originalFormValues,
		formArrayDefinition,
		formObjectDefinition,
		formLayout,
	};
}

export { useFormSchema };
